import { ThermometerReading } from '../version.service';
import { LinkWindow } from '../shared/deep-link.service';
import { uiLocale } from '../shared/chart.utils';
import { knxName, knxReadingName } from '../shared/knx-names';
import { BoardLoader, FieldStats, VALVE_STALE_MS, XY, valveFromArchive } from './board-data';

/**
 * The Analyse board's valve overview: 'valves.all' (a heatmap of every room's valve over the window and an
 * evaluation per room below it) and 'valves.<floor>' (one line per room of the floor). The rooms come from the
 * live thermometers frame (KNX rooms with a mapped valve series, in the backend's order, grouped by floor), the
 * positions from the valve archive time-weighted (agg=time, the same requests as the room charts), the
 * evaluation from /api/history/stats over the window: the valve's time-weighted mean and value histogram
 * (hist5) and the room's mean Ist and Soll.
 */

/** A KNX room of the overview. */
export interface ValveRoom {
  deviceId: string;
  /** "E1" ('' when unplaced). */
  floor: string;
  /** "WC" ('' when unplaced: then `name`). */
  room: string;
  /** The reading's own name ("E1 WC"). */
  name: string;
  /** The mapped valve's series ("knxga-16-1-3"), null: no valve. */
  series: string | null;
  /** A learned, unconfirmed mapping (valveStatus 'likely'): marked "≈". */
  likely: boolean;
  /** A 2-point / PWM valve (charted as its 30-min duty). */
  pwm: boolean;
}

/** A room's valve over the window. */
export interface ValveRow extends ValveRoom {
  /** Held positions (each until the next point; a null starts a gap) or a PWM duty line, 0..100 %. */
  line: XY[];
  /** Positions that hold (stepped 'before'); false: a PWM duty line. */
  stepped: boolean;
}

/** The KNX rooms of `floor` (null: all), grouped by floor in the order the floors first appear; with
 *  `withoutValve` also the rooms that have none. */
export function valveRooms(readings: ThermometerReading[], floor: string | null, withoutValve = false): ValveRoom[] {
  const floors = new Map<string, ValveRoom[]>();
  for (const r of readings) {
    if (r.source !== 'knx' || (!r.valveSeries && !withoutValve)) continue;
    const f = r.floor ?? '';
    if (floor != null && f !== floor) continue;
    if (!floors.has(f)) floors.set(f, []);
    floors.get(f)!.push({
      deviceId: r.deviceId, floor: f, room: r.room ?? '', name: r.name, series: r.valveSeries ?? null,
      likely: r.valveStatus === 'likely', pwm: r.valvePwm === true,
    });
  }
  return [...floors.values()].flat();
}

/** The floors that have a room with a valve ("E1" … in order). */
export function valveFloors(readings: ThermometerReading[]): string[] {
  return [...new Set(valveRooms(readings, null).map(r => r.floor).filter(f => !!f))];
}

/** What a valve chart was loaded with (rooms and their series): a change (a mapping arrived) reloads it. */
export function valveRoomsKey(readings: ThermometerReading[], floor: string | null): string {
  return valveRooms(readings, floor, true).map(r => `${r.deviceId}=${r.series ?? ''}${r.likely ? '~' : ''}`).join('|');
}

/** "E1 WC" / "E1 Toilet" (`short`: "WC", the floor charts' legend), "≈ " before a learned mapping. */
export function valveLabel(r: ValveRoom, lang: string, short = false): string {
  const name = short && r.room ? knxName(r.room, lang, 'room') : knxReadingName(r, lang);
  return (r.likely ? '≈ ' : '') + name;
}

/** The UI language (AppComponent keeps <html lang> on it). */
export function uiLang(): string {
  return document.documentElement.lang || 'de';
}

/** The valves of `floor` (null: all) over `win` at `buckets` (agg=time, as the room charts: shared requests). */
export async function loadValves(floor: string | null, win: LinkWindow, buckets: number, loader: BoardLoader,
                                 readings: ThermometerReading[]): Promise<ValveRow[]> {
  const rooms = valveRooms(readings, floor);
  // from a little before the window: the position valid at its start
  const valveWin: LinkWindow = { from: win.from - VALVE_STALE_MS, to: win.to };
  let failed = 0;
  const rows = await Promise.all(rooms.map(async (r): Promise<ValveRow> => {
    const raw = await loader.history(r.series!, valveWin, buckets, 'time').catch(() => { failed++; return null; });
    const a = raw ? valveFromArchive(raw, r.pwm, valveWin, buckets, win) : null;
    return { ...r, line: a?.line ?? [], stepped: a ? !a.pwm : true };
  }));
  if (rooms.length && failed === rooms.length) throw new Error('no valve archive readable');
  return rows;
}

/** The held value of a row at `ts` (the last point at or before it), null in a gap / before the first. */
export function valveAt(line: XY[], ts: number): number | null {
  let lo = 0, hi = line.length - 1, k = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (line[mid].x <= ts) { k = mid; lo = mid + 1; } else hi = mid - 1;
  }
  if (k < 0) return null;
  // the last point of a held value only ends it (the window's end / now)
  if (k === line.length - 1 && line.length > 1 && ts > line[k].x) return null;
  const y = line[k].y;
  return y != null && isFinite(y) ? y : null;
}

// ── evaluation ────────────────────────────────────────────────────────────────

/** One room of the evaluation over the window. */
export interface ValveEvalRow extends ValveRoom {
  /** Time-weighted mean opening (%), null: no value known in the window. */
  mean: number | null;
  /** Shares of the known time ≥ 90 % open and < 5 % open (closed), 0..1. */
  open: number | null;
  closed: number | null;
  /** Mean Ist − mean Soll (K), null without both. */
  dt: number | null;
  /** The valve's stats could not be read (an older backend, or the request failed). */
  failed: boolean;
}

export interface ValveEval {
  rows: ValveEvalRow[];
  /** No valve's stats readable (the backend has no /api/history/stats yet). */
  unavailable: boolean;
}

/** Time share of the 5 % bins `from`..`to` (indices 0..19) of a hist5, null without one. */
function share(hist: number[] | undefined, from: number, to: number): number | null {
  if (!Array.isArray(hist) || hist.length !== 20) return null;
  let s = 0, all = 0;
  for (let i = 0; i < 20; i++) { const v = Number(hist[i]) || 0; all += v; if (i >= from && i <= to) s += v; }
  return all > 0 ? s / all : null;
}

const finite = (v: unknown): number | null => typeof v === 'number' && isFinite(v) ? v : null;

/** The evaluation of every KNX room over `win` (rooms without a valve too: their Ist − Soll). */
export async function loadValveEval(win: LinkWindow, loader: BoardLoader, readings: ThermometerReading[]): Promise<ValveEval> {
  const rooms = valveRooms(readings, null, true);
  const rows = await Promise.all(rooms.map(async (r): Promise<ValveEvalRow> => {
    const [valve, room] = await Promise.all([
      r.series ? loader.stats(r.series, win, 'time') : Promise.resolve(null),
      loader.stats('thermo-' + r.deviceId, win),
    ]);
    const v: FieldStats | undefined = valve?.fields?.['value'];
    const mean = finite(v?.mean);
    const ist = finite(room?.fields?.['tempC']?.mean), soll = finite(room?.fields?.['setpointC']?.mean);
    return {
      ...r,
      mean: mean == null ? null : Math.max(0, Math.min(100, mean)),
      // bins 90–95 and 95–100 (100 % included) / bin 0–5
      open: share(v?.hist5, 18, 19),
      closed: share(v?.hist5, 0, 0),
      dt: ist != null && soll != null && soll > 0 ? ist - soll : null,
      failed: !!r.series && !valve,
    };
  }));
  const withValve = rows.filter(r => r.series);
  return { rows, unavailable: withValve.length > 0 && withValve.every(r => r.failed) };
}

/** Sort keys of the evaluation table ("-" first: descending); 'floor' = the rooms' order. */
export type ValveSort = 'floor' | 'mean' | '-mean' | 'open' | '-open' | 'closed' | '-closed' | 'dt' | '-dt';
export const VALVE_SORTS: readonly ValveSort[] = ['floor', 'mean', '-mean', 'open', '-open', 'closed', '-closed', 'dt', '-dt'];

/** The next sort after a click on column `col`: its natural direction first (shares and Ø: most first;
 *  Ist − Soll: coldest first), then the other, then the rooms' order again. */
export function nextValveSort(cur: ValveSort, col: 'floor' | 'mean' | 'open' | 'closed' | 'dt'): ValveSort {
  if (col === 'floor') return 'floor';
  const first: ValveSort = col === 'dt' ? 'dt' : `-${col}` as ValveSort;
  const second: ValveSort = col === 'dt' ? '-dt' : col as ValveSort;
  return cur === first ? second : cur === second ? 'floor' : first;
}

/** The rows in the order of `sort` (unknown values last, ties in the rooms' order). */
export function sortValveEval(rows: ValveEvalRow[], sort: ValveSort): ValveEvalRow[] {
  if (sort === 'floor') return rows;
  const desc = sort.startsWith('-');
  const key = (desc ? sort.slice(1) : sort) as 'mean' | 'open' | 'closed' | 'dt';
  const idx = new Map(rows.map((r, i) => [r, i]));
  return [...rows].sort((a, b) => {
    const x = a[key], y = b[key];
    if (x == null || y == null) return x == null && y == null ? idx.get(a)! - idx.get(b)! : x == null ? 1 : -1;
    return (desc ? y - x : x - y) || idx.get(a)! - idx.get(b)!;
  });
}

// ── evaluation as shown (table on screen, lines in the PDF) ─────────────────────

/** One room of the evaluation as shown: texts in the UI locale, bar widths in % of their cell. */
export interface ValveEvalCells {
  id: string;
  /** "E1 WC" ("≈ " before a learned mapping). */
  label: string;
  likely: boolean;
  hasValve: boolean;
  /** The first room of another floor (in the rooms' order): a separator above it. */
  floorStart: boolean;
  /** No stats for this valve (an older backend, a failed request). */
  failed: boolean;
  mean: string; meanW: number;
  open: string; openW: number;
  closed: string; closedW: number;
  /** Ist − Soll ("−0,4 K"); its bar from the cell's middle (±3 K = the half width), cold = below Soll. */
  dt: string; dtLeft: number; dtW: number; dtCold: boolean;
  /** Ø opening 0..100 (the heatmap colour of the PDF line), null: unknown. */
  meanPct: number | null;
}

/** Ist − Soll that fills half the bar. */
const DT_FULL_K = 3;

const nf = new Map<string, Intl.NumberFormat>();
function num(v: number, digits: number): string {
  const loc = uiLocale();
  const key = loc + digits;
  let f = nf.get(key);
  if (!f) nf.set(key, f = new Intl.NumberFormat(loc, { minimumFractionDigits: digits, maximumFractionDigits: digits }));
  return f.format(v);
}

/** A share 0..1 as "12 %" ("< 1 %" for a trace, "–" unknown). */
function shareText(v: number | null): string {
  if (v == null) return '–';
  return v > 0 && v < 0.005 ? '< 1 %' : `${num(Math.round(v * 100), 0)} %`;
}

/** "+0,4 K" / "−1,2 K" / "±0,0 K" ("–" unknown). */
export function dtText(v: number | null): string {
  if (v == null) return '–';
  const r = Math.round(v * 10) / 10;
  return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${num(Math.abs(r), 1)} K`;
}

/** The rows of `ev` in the order of `sort`, as shown. */
export function valveEvalCells(ev: ValveEval, sort: ValveSort, lang: string): ValveEvalCells[] {
  return sortValveEval(ev.rows, sort).map((r, i, all) => {
    const dt = r.dt;
    const dtW = dt == null ? 0 : Math.min(50, Math.abs(dt) / DT_FULL_K * 50);
    return {
      id: r.deviceId,
      label: valveLabel(r, lang),
      likely: r.likely,
      hasValve: !!r.series,
      floorStart: sort === 'floor' && i > 0 && all[i - 1].floor !== r.floor,
      failed: r.failed,
      mean: r.mean == null ? '–' : `${num(Math.round(r.mean), 0)} %`, meanW: r.mean ?? 0,
      open: shareText(r.open), openW: (r.open ?? 0) * 100,
      closed: shareText(r.closed), closedW: (r.closed ?? 0) * 100,
      dt: dtText(dt), dtLeft: dt != null && dt < 0 ? 50 - dtW : 50, dtW, dtCold: dt != null && dt < 0,
      meanPct: r.mean,
    };
  });
}
